import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import JsonLd from '@/components/base/JsonLd';
import { getSiteUrl } from '@/utils/seo';

export default function ArticlePage() {
  const { t } = useTranslation();
  const siteUrl = getSiteUrl();

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: t('article.title'),
    description: t('article.meta_desc'),
    image: 'https://readdy.ai/api/search-image?query=Clean%20minimalist%20workspace%20photography%20with%20an%20open%20notebook%20featuring%20handwritten%20notes%20next%20to%20a%20ceramic%20cup%20of%20tea%20on%20a%20warm%20oak%20desk%20surface%2C%20soft%20morning%20sunlight%20streaming%20through%20a%20nearby%20window%20creating%20gentle%20shadows%2C%20cozy%20productive%20atmosphere%20with%20natural%20wood%20tones%20and%20cream%20textiles%2C%20Scandinavian%20inspired%20home%20office%20aesthetic&width=600&height=450&seq=kg-art-card1-v2&orientation=landscape',
    datePublished: '2026-07-07',
    dateModified: '2026-07-07',
    author: {
      '@type': 'Organization',
      name: 'KakobuyFinds',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'KakobuyFinds',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': siteUrl + '/artykul/kakobuy-sheet-poradnik',
    },
    inLanguage: 'pl',
  };

  return (
    <main className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <header className="sticky top-0 z-40 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="max-w-4xl mx-auto px-4 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="font-heading text-lg font-bold text-foreground-900 tracking-tight whitespace-nowrap">
            kakobuyfinds<span className="text-primary-500">.website</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            {t('article.back_home')}
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 md:px-10 py-12 md:py-16">
        <header className="mb-10">
          <div className="flex items-center gap-4 text-sm text-foreground-500 mb-4">
            <time dateTime="2026-07-07">{t('article.date')}</time>
            <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
            <span>{t('article.author')}</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight">
            {t('article.title')}
          </h1>
        </header>

        <div className="prose-custom space-y-8">
          <section>
            <p className="text-lg text-foreground-700 leading-relaxed">
              {t('article.intro')}
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              {t('article.section1_title')}
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('article.section1_p1')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('article.section1_p2')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed">
              {t('article.section1_p3')}
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              {t('article.section2_title')}
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('article.section2_p1')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('article.section2_p2')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed">
              {t('article.section2_p3')}
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              {t('article.section3_title')}
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('article.section3_p1')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('article.section3_p2')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed">
              {t('article.section3_p3')}
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              {t('article.section4_title')}
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('article.section4_p1')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('article.section4_p2')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed">
              {t('article.section4_p3')}
            </p>
          </section>

          <section className="bg-background-100 rounded-2xl p-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              {t('article.conclusion_title')}
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('article.conclusion_p1')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed">
              {t('article.conclusion_p2')}{' '}
              <Link to="/" className="text-primary-600 hover:text-primary-700 underline">
                {t('article.conclusion_link')}
              </Link>
              {t('article.conclusion_suffix')}
            </p>
          </section>

          <div className="flex flex-col sm:flex-row items-center gap-4 pt-8 border-t border-background-200">
            <Link
              to="/"
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i>
              {t('article.back_home')}
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap"
            >
              Kakobuy Sheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      </article>

      <footer className="border-t border-background-200 py-8">
        <div className="max-w-3xl mx-auto px-4 md:px-10 text-center">
          <Link to="/" className="font-heading text-lg font-bold text-foreground-900 whitespace-nowrap">
            kakobuyfinds<span className="text-primary-500">.website</span>
          </Link>
          <p className="text-xs text-foreground-500 mt-2">© 2026 kakobuyfinds.website. Wszelkie prawa zastrzeżone.</p>
        </div>
      </footer>
    </main>
  );
}